import React, { useCallback, useEffect, useMemo, useState } from "react";
import { caApi } from "../../../backend_urls/caApi";

import DepartmentDrawer from "./models/departments_model/DepartmentDrawer";
import DepartmentForm from "./models/departments_model/DepartmentForm";
import DepartmentHeader from "./models/departments_model/DepartmentHeader";
import DepartmentsTable from "./models/departments_model/DepartmentsTable";
import DepartmentStatsCards from "./models/departments_model/DepartmentStatsCards";
import DepartmentToolbar from "./models/departments_model/DepartmentToolbar";

const emptyFilters = { search: "", office: "", status: "" };

const getResponseData = (response) => {
    if (Array.isArray(response)) { return response; }
    if (Array.isArray(response?.data)) { return response.data; }
    if (Array.isArray(response?.results)) { return response.results; }
    if (Array.isArray(response?.data?.results)) { return response.data.results; }

    return [];
};

const CADepartments = () => {
    const [departments, setDepartments] = useState([]);
    const [employees, setEmployees] = useState([]);
    const [tasks, setTasks] = useState([]);

    const [filters, setFilters] = useState(emptyFilters);

    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [error, setError] = useState("");

    const [selectedDepartment, setSelectedDepartment] = useState(null);

    const [drawerOpen, setDrawerOpen] = useState(false);

    const [showForm, setShowForm] = useState(false);

    const [editingDepartment, setEditingDepartment] = useState(null);

    const loadData = useCallback(async (isRefresh = false) => {

        try {
            if (isRefresh) { setRefreshing(true);
            } else { setLoading(true); }

            setError("");

            const [
                departmentData,
                employeeData,
                taskData,
            ] = await Promise.all([
                caApi.getDepartments(),
                caApi.getEmployees(),
                caApi.getTasks(),
            ]);

            setDepartments(getResponseData(departmentData));
            setEmployees(getResponseData(employeeData));
            setTasks(getResponseData(taskData));
        } catch (err) {
            console.error("Failed to load department data:", err);

            setError(
                err?.response?.data?.detail ||
                "Failed to load department data."
            );
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    }, []);

    useEffect(() => { loadData(); }, [loadData]);

    const getDepartmentEmployees = useCallback(
        (departmentId) => {
            return employees.filter(
                (employee) =>
                    Number(employee.department) ===
                    Number(departmentId)
            );
        },
        [employees]
    );

    const getDepartmentTasks = useCallback(
        (departmentId) => {
            return tasks.filter(
                (task) =>
                    Number(task.department) ===
                    Number(departmentId)
            );
        },
        [tasks]
    );

    const enrichedDepartments = useMemo(() => {
        return departments.map((department) => {
            const departmentEmployees = getDepartmentEmployees(department.id);
            const departmentTasks = getDepartmentTasks(department.id);

            const activeTasks =
                departmentTasks.filter(
                    (task) =>
                        ![
                            "completed",
                            "cancelled",
                        ].includes(task.status)
                );

            const completedTasks =
                departmentTasks.filter(
                    (task) => task.status === "completed"
                );

            const completionRate =
                departmentTasks.length > 0
                    ? Math.round(
                        (
                            completedTasks.length /
                            departmentTasks.length
                        ) * 100
                    )
                    : 0;

            return {
                ...department,

                office: department.office_name || "—",
                status: department.is_active ? "Active" : "Inactive",
                employees: departmentEmployees.length,
                activeTasks: activeTasks.length,
                completedTasks: completedTasks.length,
                completionRate,
            };
        });

    }, [
        departments,
        getDepartmentEmployees,
        getDepartmentTasks,
    ]);


    const filteredDepartments = useMemo(() => {
        const search = filters.search.trim().toLowerCase();

        return enrichedDepartments.filter(
            (department) => {
                const matchesSearch =
                    !search ||
                    department.name
                        ?.toLowerCase()
                        .includes(search) ||
                    department.code
                        ?.toLowerCase()
                        .includes(search) ||
                    department.description
                        ?.toLowerCase()
                        .includes(search);

                const matchesOffice =
                    !filters.office ||
                    department.office ===
                    filters.office;

                const matchesStatus =
                    !filters.status ||
                    department.status ===
                    filters.status;

                return (
                    matchesSearch &&
                    matchesOffice &&
                    matchesStatus
                );
            }
        );
    }, [ enrichedDepartments, filters ]);

    const stats = useMemo(() => {
        const activeDepartments =
            enrichedDepartments.filter(
                (department) => department.is_active
            );

        const openTasks =
            tasks.filter(
                (task) =>
                    ![
                        "completed",
                        "cancelled",
                    ].includes(task.status)
            );

        const completedToday =
            tasks.filter((task) => {
                if (task.status !== "completed") { return false; }

                const completedAt =
                    task.completed_at ||
                    task.updated_at;

                if (!completedAt) { return false; }

                const today = new Date();
                const date = new Date(completedAt);

                return (
                    date.getFullYear() ===
                        today.getFullYear() &&
                    date.getMonth() ===
                        today.getMonth() &&
                    date.getDate() ===
                        today.getDate()
                );
            });

        const totalDepartmentTasks =
            enrichedDepartments.reduce(
                (total, department) =>
                    total +
                    (
                        department.activeTasks +
                        department.completedTasks
                    ),
                0
            );

        const completedDepartmentTasks =
            enrichedDepartments.reduce(
                (total, department) =>
                    total +
                    department.completedTasks,
                0
            );

        const completionRate =
            totalDepartmentTasks > 0
                ? Math.round(
                    (
                        completedDepartmentTasks /
                        totalDepartmentTasks
                    ) * 100
                )
                : 0;

        return {
            departments: activeDepartments.length,
            employees: employees.length,
            openTasks: openTasks.length,
            completedToday: completedToday.length,
            completionRate,
        };
    }, [ enrichedDepartments, employees, tasks ]);

    const officeOptions = useMemo(() => {
        return [
            ...new Set(
                enrichedDepartments
                    .map((department) => department.office)
                    .filter((office) => office && office !== "—")
            ),
        ].sort();
    }, [enrichedDepartments]);

    const handleViewDepartment = (department) => {
        setSelectedDepartment(department);
        setDrawerOpen(true);
    };

    const handleCreateDepartment = () => {
        setEditingDepartment(null);
        setShowForm(true);
    };

    const handleEditDepartment = (department) => {
        setDrawerOpen(false);
        setEditingDepartment(department);
        setShowForm(true);
    };

    const handleDeleteDepartment = async (department) => {
        const confirmed = window.confirm(`Deactivate "${department.name}"?`);
        if (!confirmed) { return; }
        try {
            await caApi.deleteDepartment(department.id);
            setDrawerOpen(false);
            await loadData(true);
        } catch (err) {
            console.error("Failed to deactivate department:", err);

            setError(
                err?.response?.data?.detail ||
                "Failed to deactivate department."
            );
        }
    };

    const handleSaveDepartment = async (payload) => {
        try {
            if (editingDepartment) {
                await caApi.updateDepartment(editingDepartment.id, payload);
            } else {
                await caApi.createDepartment(payload);
            }

            setShowForm(false);
            setEditingDepartment(null);
            await loadData(true);
        } catch (err) {
            console.error("Failed to save department:", err);
            throw err;
        }
    };

    return (
        <div>
            <DepartmentHeader
                onCreate={handleCreateDepartment}
                onRefresh={() => loadData(true)}
                refreshing={refreshing}
            />

            {error && (
                <div
                    style={{
                        marginBottom: "20px",
                        padding: "12px 16px",
                        borderRadius: "10px",
                        background: "#fef2f2",
                        border: "1px solid #fecaca",
                        color: "#b91c1c",
                    }}
                >
                    {error}
                </div>
            )}

            <DepartmentStatsCards
                stats={stats}
                loading={loading}
            />

            <DepartmentToolbar
                filters={filters}
                onChange={setFilters}
                officeOptions={officeOptions}
                onReset={() =>
                    setFilters(emptyFilters)
                }
            />

            <DepartmentsTable
                departments={filteredDepartments}
                loading={loading}
                onView={handleViewDepartment}
            />

            <DepartmentDrawer
                department={selectedDepartment}
                isOpen={drawerOpen}
                onClose={() =>
                    setDrawerOpen(false)
                }
                onEdit={handleEditDepartment}
                onDelete={handleDeleteDepartment}
            />

            {showForm && (
                <DepartmentForm
                    department={editingDepartment}
                    officeOptions={[]}
                    onClose={() => {
                        setShowForm(false);
                        setEditingDepartment(null);
                    }}
                    onSave={handleSaveDepartment}
                />
            )}
        </div>
    );
};

export default CADepartments;
